import { apiSlice } from './apiSlice';

export interface PendingUser {
  id: string | number;
  username?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
  created_at?: string;
}

export interface AdminUser {
  id: string | number;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
  role?: string | null;
  max_farms?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface AdminSystemMetricsOverview {
  farmsCount: number;
  activePlantingsCount: number;
  simSessionsTotal: number;
  simSessionsActive: number;
  simEventsTotal: number;
  cpvRealRows: number;
  cpvSimRows: number;
  stateChangesTotal: number;
  stateChanges24h: number;
  stateSnapshotsTotal: number;
  stateSnapshots24h: number;
}

export interface AdminActiveSessionMetrics {
  id: string;
  farmId: string;
  farmName: string;
  mode: string;
  status: string;
  simDate: string | null;
  advanceIntervalMs: number;
  createdAt: string;
  plantingsCount: number;
  cpvSimRows: number;
  stateChangesCount: number;
  lastSnapshotAt: string | null;
}

export interface AdminSystemMetrics {
  timestamp: string;
  process: {
    uptimeSeconds: number;
    memory: {
      rss: number;
      heapTotal: number;
      heapUsed: number;
      external: number;
      arrayBuffers: number;
    };
    nodeEnv: string;
  };
  scheduler: {
    enabled: boolean;
    intervalMs: number | null;
  };
  overview: AdminSystemMetricsOverview;
  activeSessions: AdminActiveSessionMetrics[];
  jobsOverview: {
    total: number;
    pending: number;
    running: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
  recentJobs: Array<{
    id: string;
    farmId: string;
    farmName: string;
    simSessionId: string;
    jobType: string;
    status: string;
    totalItems: number;
    processedItems: number;
    failedItems: number;
    requestedAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    errorMessage: string | null;
  }>;
}

export interface AdminStateChangeItem {
  id: string;
  createdAt: string;
  farmId: string;
  farmName: string | null;
  simSessionId: string | null;
  entityType: 'planting' | 'bed' | 'plot' | 'field' | string;
  entityId: string;
  propertyId: number;
  propertyName: string | null;
  propertyCode: string | null;
  resultingValue: string;
  sourceType: string;
  sourceEventId: string | null;
}

export interface AdminStateSnapshotItem {
  id: string;
  createdAt: string;
  farmId: string;
  farmName: string | null;
  simSessionId: string | null;
  entityType: 'planting' | 'bed' | 'plot' | 'field' | string;
  entityId: string;
  reason: string;
  lane: string | null;
  simDate: string | null;
  snapshotData: string | null;
  snapshotPreview: string | null;
}

export interface AdminListResponse<T> {
  total: number;
  items: T[];
}

export const adminApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getPendingUsers: build.query<PendingUser[], void>({
      query: () => ({ url: '/admin/pending-users' }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'AdminPendingUsers' as const, id: String(item.id) })),
              { type: 'AdminPendingUsers' as const, id: 'LIST' },
            ]
          : [{ type: 'AdminPendingUsers' as const, id: 'LIST' }],
    }),
    updatePendingUserStatus: build.mutation<void, { id: string | number; status: string }>({
      query: ({ id, status }) => ({
        url: `/admin/pending-users/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminPendingUsers' as const, id: String(id) },
        { type: 'AdminPendingUsers' as const, id: 'LIST' },
        { type: 'AdminUsers' as const, id: 'LIST' },
      ],
    }),
    getUsers: build.query<AdminUser[], void>({
      query: () => ({ url: '/admin/users' }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'AdminUsers' as const, id: String(item.id) })),
              { type: 'AdminUsers' as const, id: 'LIST' },
            ]
          : [{ type: 'AdminUsers' as const, id: 'LIST' }],
    }),
    updateUserMaxFarms: build.mutation<void, { id: string | number; max_farms: number }>({
      query: ({ id, max_farms }) => ({
        url: `/admin/users/${id}/max-farms`,
        method: 'PATCH',
        body: { max_farms },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminUsers' as const, id: String(id) },
        { type: 'AdminUsers' as const, id: 'LIST' },
      ],
    }),
    getSystemMetrics: build.query<AdminSystemMetrics, void>({
      query: () => ({ url: '/admin/system-metrics' }),
    }),
    getStateChanges: build.query<AdminListResponse<AdminStateChangeItem>, { farmId?: string; simSessionId?: string; limit?: number } | void>({
      query: (params) => ({
        url: '/admin/state-changes',
        params: params || undefined,
      }),
    }),
    getStateSnapshots: build.query<AdminListResponse<AdminStateSnapshotItem>, { farmId?: string; simSessionId?: string; limit?: number } | void>({
      query: (params) => ({
        url: '/admin/state-snapshots',
        params: params || undefined,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPendingUsersQuery,
  useUpdatePendingUserStatusMutation,
  useGetUsersQuery,
  useUpdateUserMaxFarmsMutation,
  useGetSystemMetricsQuery,
  useGetStateChangesQuery,
  useGetStateSnapshotsQuery,
} = adminApi;
